import queryString from 'query-string';
import http from '../httpConfig';
import { API_ENDPOINTS } from '../utils/apiConstant';
import { ListPayload, Task, VisitDocument } from '../interfaces/property-visit.interface';
import {
  mapPropertyVisitList,
  mapLookupList,
  mapActiveGuest,
  PropertyVisitListResponse,
} from '../mappers/property-visit.mapper';

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------



// ---------------------------------------------------------------------------
// Service methods
// ---------------------------------------------------------------------------

const list = async (payload: ListPayload): Promise<PropertyVisitListResponse> => {
  const { searchText, propertyId, visitType, userRole, visitedBy, visitStatus, startDate, endDate, managedByUserId, page, perPage, orderBy, order } = payload;
  const response = await http.post(API_ENDPOINTS.listPropertyVisits, {
    search: searchText || undefined,
    property_id: propertyId || undefined,
    visit_type: visitType || undefined,
    user_role: userRole || undefined,
    visited_by: visitedBy || undefined,
    status: visitStatus || undefined,
    date_from: startDate || undefined,
    date_to: endDate || undefined,
    managed_by_user_id: managedByUserId || undefined,
    page,
    per_page: perPage,
    order_by: orderBy || undefined,
    order: order || undefined,
  });
  return mapPropertyVisitList(response);
};

const get = async (id: string): Promise<any> => {
  const response = await http.get(`${API_ENDPOINTS.propertyVisits}/${id}`);
  return response;
};

const add = async (payload: any): Promise<{ success: boolean; id: string }> => {
  const response = await http.post(API_ENDPOINTS.propertyVisits, payload);
  return response as any;
};

const edit = async (id: string, payload: any): Promise<{ success: boolean }> => {
  const response = await http.put(`${API_ENDPOINTS.propertyVisits}/${id}`, payload);
  return response as any;
};

const deleteVisit = async (id: string): Promise<{ success: boolean }> => {
  const response = await http.delete(`${API_ENDPOINTS.propertyVisits}/${id}`);
  return response as any;
};

const visitedByLookup = async (payload: { searchText: string; role?: string; roleId?: string; cleanerServiceTypeId?: string }): Promise<{ records: any[] }> => {
  if (!payload.role) return { records: [] };

  if (payload.role === 'cleaner') {
    if (!payload.cleanerServiceTypeId) return { records: [] };
    const url = `${API_ENDPOINTS.lookupVendors}?${queryString.stringify({ search: payload.searchText || undefined, service_type_id: payload.cleanerServiceTypeId, is_active: 1 })}`;
    const response = await http.get(url);
    return { records: mapLookupList(response) };
  }

  if (!payload.roleId) return { records: [] };
  const url = `${API_ENDPOINTS.lookupUsers}?${queryString.stringify({ search: payload.searchText || undefined, role_id: payload.roleId, is_active: '1' })}`;
  const response = await http.get(url);
  return { records: mapLookupList(response) };
};

const getVisitTasks = async (visitId: string): Promise<Task[]> => {
  const url = `${API_ENDPOINTS.propertyVisitTasks(visitId)}?${queryString.stringify({ per_page: 100 })}`;
  const response: any = await http.get(url);
  const raw = response?.data ?? response?.records ?? response;
  const records = Array.isArray(raw) ? raw : [];
  return records.map((t: any) => ({
    id: t.id?.toString() ?? '',
    description: t.description ?? '',
    category: t.category ?? '',
    category_name: t.category_name ?? '',
    priority: t.priority ?? '',
    priority_name: t.priority_name ?? '',
    status: t.status ?? '',
    status_name: t.status_name ?? '',
    due_date: t.due_date ?? '',
  }));
};

const addVisitTask = async (visitId: string, payload: any): Promise<{ success: boolean; data?: any }> => {
  const response = await http.post(API_ENDPOINTS.propertyVisitTask(visitId), {
    description: payload.description,
    category: payload.category,
    priority: payload.priority,
    status: payload.status || undefined,
    due_date: payload.due_date,
  });
  return response as any;
};

const editVisitTask = async (visitId: string, taskId: string, payload: any): Promise<{ success: boolean }> => {
  const response = await http.put(API_ENDPOINTS.propertyVisitTaskById(visitId, taskId), {
    description: payload.description,
    category: payload.category,
    priority: payload.priority,
    status: payload.status,
    due_date: payload.due_date,
  });
  return response as any;
};

const deleteVisitTask = async (visitId: string, taskId: string): Promise<{ success: boolean }> => {
  const response = await http.delete(API_ENDPOINTS.propertyVisitTaskById(visitId, taskId));
  return response as any;
};

const checkActiveGuest = async (propertyId: string, visitDate: string) => {
  const url = `${API_ENDPOINTS.checkActiveGuest}?${queryString.stringify({ property_id: propertyId, visit_date: visitDate })}`;
  const response = await http.get(url);
  return mapActiveGuest(response);
};

const addUrlDocument = async (visitId: string, url: string): Promise<{ success: boolean; data?: VisitDocument }> => {
  const response = await http.post(API_ENDPOINTS.propertyVisitDocument(visitId), { document_type: 'url', url });
  return response as any;
};

const deleteDocument = async (visitId: string, docId: string): Promise<{ success: boolean }> => {
  const response = await http.delete(API_ENDPOINTS.propertyVisitDocumentById(visitId, docId));
  return response as any;
};

const CHUNK_SIZE = 2 * 1024 * 1024;

const initUpload = async (payload: { visit_id: string; file_name: string; document_type: 'pdf' | 'image'; total_chunks: number }): Promise<{ upload_id: string }> => {
  const response = await http.post(API_ENDPOINTS.propertyVisitUploadInit, payload);
  return response as any;
};

const uploadChunk = async (uploadId: string, chunkIndex: number, chunk: Blob): Promise<{ success: boolean }> => {
  const formData = new FormData();
  formData.append('upload_id', uploadId);
  formData.append('chunk_index', String(chunkIndex));
  formData.append('chunk', chunk, 'chunk');
  const response = await http.post(API_ENDPOINTS.propertyVisitUploadChunk, formData, {
    headers: { 'Content-Type': undefined as any },
  });
  return response as any;
};

const completeUpload = async (uploadId: string): Promise<VisitDocument> => {
  const response = await http.post(API_ENDPOINTS.propertyVisitUploadComplete, { upload_id: uploadId });
  return response as any;
};

const cancelUpload = async (uploadId: string): Promise<void> => {
  await http.delete(API_ENDPOINTS.propertyVisitUploadCancel(uploadId));
};

const chunkedUploadFile = async (file: File, docType: 'pdf' | 'image', visitId: string): Promise<VisitDocument | null> => {
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  const initRes = await initUpload({ visit_id: visitId, file_name: file.name, document_type: docType, total_chunks: totalChunks });
  const uploadId = (initRes as any)?.upload_id;
  if (!uploadId) return null;
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    await uploadChunk(uploadId, i, file.slice(start, end));
  }
  return completeUpload(uploadId);
};

const PropertyVisitService = {
  list,
  get,
  add,
  edit,
  deleteVisit,
  visitedByLookup,
  getVisitTasks,
  addVisitTask,
  editVisitTask,
  deleteVisitTask,
  checkActiveGuest,
  addUrlDocument,
  deleteDocument,
  initUpload,
  uploadChunk,
  completeUpload,
  cancelUpload,
  chunkedUploadFile,
};

export default PropertyVisitService;
